import {stringify} from 'query-string';
import {ReplaySubject, BehaviorSubject, Subject, combineLatest, firstValueFrom, of, Observable, merge, defer} from 'rxjs';
import {distinctUntilChanged, filter, map, shareReplay, skipUntil, switchMap, take, withLatestFrom} from 'rxjs/operators';
import {get} from 'svelte/store';
import {PATHS} from '@/const/paths';
import {shouldLogin} from '@/config/white-list';
import {mc} from '@/core';
import {envUtil} from '@/helper/env';
import {langUtil} from '@/helper/lang';
import {genSystemDevice} from '@/helper/device';
import {defaultFiatCurrencyByHost} from '@/helper/localized-site';
import {userApi} from '@/api/user';
import {web3Api} from '@/api/web3';
import {activityApi} from '@/api/activity';
import {ty} from '@/da/tingyun';
import {trackSupportEvent} from '@/da/sd';
import {lang$} from '@/store/lang';
import {appPlatform$} from '@/store/app';
import {readToken, removeToken, removeCurrentUserId, writeCurrentUserId} from '@/storage';
import {readBid} from '@/storage/bid';
import {updateUsedUIDs} from '@/storage/uids';
import {readActivationStatus, writeActivationStatus} from '@/storage/user';
import {isCtSubUser, isStrategySubUser} from './utils';
import {wwData$} from '@/streams/worker-data';
import {browser$} from './browser';
import {isActivateMarginAccount$} from './config/margin-trade';
import {isActivateLendingAccount$} from './config/lending';
import {wsOpen$} from './ws';
import {userInfo$, createInitUserInfo} from './user';
import {fiatCurrency$} from './fiat-currency';

export const login$ = new Subject();
export const logout$ = new Subject();

// 推荐使用的用户状态判断
export const isLogin$ = new BehaviorSubject(envUtil.isBrowser && !!readToken());
/**
 * 业务代码请使用 isLogin$，Init 状态仅供骨架屏首次渲染使用
 * @type {BehaviorSubject<'Init' | 'Login' | 'Logout'>}
 * */
export const userStatus$ = new BehaviorSubject(envUtil.isServer ? 'Init' : !!readToken() ? 'Login' : 'Logout');

// hooks
export const afterLogin$ = new ReplaySubject();
export const beforeLogout$ = new Subject();

export const isHaveEarnCryptoAuthority$ = new ReplaySubject();
export const activationStatus$ = new BehaviorSubject(process.browser ? readActivationStatus() || '0' : '0');

// OTC-lengend-trading 兼容
// @deprecated
//  应该使用侧边栏登录组件/配置登录页面名单来实现业务场景登录
//  不应当存在单独跳转登录的逻辑
export const gotoLogin$ = new Subject();

export const refreshUserWalletInfo$ = new Subject();
/** @type {Observable<ReturnType<typeof createWalletInfoDefaultData>>} */
export const userWalletInfo$ = merge(
  userStatus$.pipe(
    filter((userStatus) => userStatus !== 'Init'),
    map((userStatus) => userStatus === 'Login')
  ),
  refreshUserWalletInfo$.pipe(
    withLatestFrom(isLogin$.pipe(filter(Boolean))),
    map(() => true)
  )
).pipe(
  skipUntil(browser$),
  switchMap((isLogin) =>
    !isLogin
      ? of({})
      : // address优先从user里提取
        merge(
          userInfo$.pipe(
            take(1),
            map((userInfo) => userInfo.address),
            filter(Boolean),
            map((address) => ({data: {...createWalletInfoDefaultData(), address}}))
          ),
          defer(web3Api.getWeb3Info)
        )
  ),
  map((response) => response.data || createWalletInfoDefaultData()),
  shareReplay(1)
);

const checkToken$ = new Subject();

let fireBaseToken = '';
let fetchFireBaseTokenTimeout;

isLogin$.subscribe((isLogin) => {
  const oldVal = userStatus$.getValue() === 'Login';
  if (oldVal !== isLogin) {
    userStatus$.next(isLogin ? 'Login' : 'Logout');
  }
});

/**
 * 登录后只要 ws open (包括断线重连)，就需要重新认证
 */
combineLatest([login$, wsOpen$]).subscribe(doWsAuth);

activationStatus$.subscribe((status) => {
  process.browser && writeActivationStatus(status);
});

login$.subscribe((userInfo) => {
  // 先更新数据，后更新登录状态
  const {userId} = userInfo;
  ty.setUid(userId);
  writeCurrentUserId(userId);
  userInfo$.next(userInfo);
  wwData$.send({
    type: 'sub-users-subscribe',
    users: {
      main: userInfo,
      subs: (userInfo.subUsers || []).filter((u) => isStrategySubUser(u) || isCtSubUser(u)),
    },
  });
  isHaveEarnCryptoAuthority$.next(userInfo.roles.indexOf(14) > -1);
  fiatCurrency$.next(userInfo.currency || defaultFiatCurrencyByHost);

  if ((!userInfo.uta || userInfo.uta.spot !== 2) && userInfo.marginTradingAccountInfo && userInfo.marginTradingAccountInfo.marginAccountStatus === 1) {
    // 0:未开户 1:正常使用
    isActivateMarginAccount$.next(true);
  }
  isActivateLendingAccount$.next(!!userInfo.lendingAccount && userInfo.lendingAccount.status === 1); // 0:未开户 1:正常使用
  startFetchFirebaseToken();
  updateUsedUIDs(userId);
  trackSupportEvent('indexedDB');
  wwData$.send({
    type: 'init-user-data',
    data: userInfo,
  });
  if (!isLogin$.getValue()) {
    isLogin$.next(true);
  }
  afterLogin$.next();
});

logout$.subscribe(async ({manual, code, redirectToLogin}) => {
  beforeLogout$.next();
  // 先设置登录状态，后清空数据
  if (isLogin$.getValue()) {
    isLogin$.next(false);
  }
  userInfo$.next(createInitUserInfo());
  isHaveEarnCryptoAuthority$.next(false);
  fiatCurrency$.next(defaultFiatCurrencyByHost);
  removeCurrentUserId();
  clearTimeout(fetchFireBaseTokenTimeout);
  if (manual) {
    await userApi.logout();
  }
  removeToken();
  // 删除新人奖励的关闭时间, 活动长期存在
  localStorage.removeItem('WELCOME_LAST_CLOSE_TIME');
  localStorage.removeItem('welcomeGuideDestoryTime');
  try {
    await deleteFirebaseToken();
  } catch (e) {
    console.error(e);
  }
  if (envUtil.isApp || redirectToLogin === false) {
    return;
  }
  const needLogin = shouldLogin(location.pathname);
  if (needLogin) {
    gotoLogin({target: genTarget(location), code});
    return;
  }
  // 3401 token 非法，走补偿逻辑 => 直接刷新页面，以非登录态尝试访问
  if (manual || code === 3401) {
    location.reload();
  }
});

// 2. OTC-lengend-trading 专用
gotoLogin$.subscribe(({target, code}) => gotoLogin({target, code}));

checkToken$.pipe(switchMap(() => userApi.getSelf())).subscribe(({data, error}) => {
  if (data) {
    login$.next(data);
    lang$.set(data.lang);
  }
  if (error && error.code === 3401) {
    logout$.next({code: error.code});
  }
});

mc.subscribe('http', '401', ({reply}) => {
  logout$.next({code: reply.code});
});

mc.subscribe('app-token', 'update', () => {
  checkToken$.next();
});

/**
 * entry point
 */
browser$.subscribe(() => {
  if (envUtil.isApp) {
    return;
  }

  const token = readToken();
  if (token) {
    checkToken$.next();
    return;
  }
  // 检查页面是否可以不登录访问
  const needLogin = shouldLogin(location.pathname);
  if (needLogin) {
    gotoLogin({target: genTarget(location), code: 0});
  }
});

function doWsAuth() {
  wwData$.send({type: 'token-update', platform: readPlatform(), token: readToken()});
}

function gotoLogin({target, code = 0} = {}) {
  const language = get(lang$);
  const link = (language === 'en' ? '' : '/' + language) + PATHS.LOGIN.pathname;
  location.assign([link, '?', stringify({target, code})].join(''));
}

function genTarget(location) {
  const path = langUtil.parsePathname(location.pathname);
  // logout 页面 logout 后，不需要记住
  return path.pathname === PATHS.LOGOUT.pathname ? undefined : location.href;
}

export function readPlatform() {
  if (envUtil.isApp) {
    return get(appPlatform$).toUpperCase();
  }

  return 'PC';
}

function startFetchFirebaseToken() {
  clearTimeout(fetchFireBaseTokenTimeout);
  fetchFirebaseToken();
  // 1小时内，手动更新
  fetchFireBaseTokenTimeout = setTimeout(startFetchFirebaseToken, 60 * 60 * 1000);
}

function fetchFirebaseToken() {
  let vapidKey = 'BCMf7Vi9K3D5SBt0prMfEYLUknTfesw7s2AHFI6zzX8zg8R6_D0l_ej94ABiPbPiPcuPvmKt0KLT_gv8NeZGJjs';
  if (envUtil.isProduction) {
    vapidKey = 'BKd2dJP2z876gTGS916kVjHprTDfEpASqQJiOwBSb8tF553s4bsOMfmmY4sCu7QAqwVc-gvwFuSbQbcgsw_UW40';
  }
  waitForNotificationPermission()
    .then(() => {
      if (window.firebase) {
        firebaseMessaging.getToken({vapidKey}).then((currentToken) => {
          if (currentToken) {
            fireBaseToken = currentToken;
            if (isFirebaseTokenNeedChanged(currentToken)) {
              activityApi.updatePushDevice(currentToken, get(lang$), readBid(), genSystemDevice()).then(() => {
                storeFirebaseToken(currentToken);
              });
            }
          }
        });
      }
    })
    .catch(() => {
      console.log('Notification permission not granted');
    });
}

async function waitForNotificationPermission() {
  if (Notification.permission === 'granted') {
    return Promise.resolve();
  }
  const permission = await Notification.requestPermission();
  if (permission === 'granted') {
    return Promise.resolve();
  }
  return Promise.reject();
}

function storeFirebaseToken(token) {
  localStorage.setItem('firebase-token', token);
  localStorage.setItem('firebase-token-timestamp', Date.now().toString());
}

function isFirebaseTokenNeedChanged(token) {
  if (Date.now() - (localStorage.getItem('firebase-token-timestamp') || 0) > 60 * 60 * 1000) {
    return true;
  }
  return localStorage.getItem('firebase-token') !== token;
}

function clearFirebaseToken() {
  localStorage.removeItem('firebase-token');
  localStorage.removeItem('firebase-token-timestamp');
}

async function deleteFirebaseToken() {
  clearFirebaseToken();
  if (Notification.permission === 'granted' && fireBaseToken && window.firebase) {
    await firebaseMessaging.deleteToken(fireBaseToken);
  }
}

function createWalletInfoDefaultData() {
  return {address: '', walletSource: '', walletName: ''};
}

export function withAuth(request) {
  return isLogin$.pipe(filter(Boolean), take(1), switchMap(request));
}

export function waitAuth() {
  return firstValueFrom(isLogin$.pipe(filter(Boolean)));
}

/** 快速响应登录态，但是状态有可能会变化（概率比较小） */
export const isLoginSnapshot$ = merge(
  browser$.pipe(map(() => !!readToken())),
  userStatus$.pipe(
    filter((userStatus) => userStatus !== 'Init'),
    map((userStatus) => userStatus === 'Login')
  )
).pipe(distinctUntilChanged(), shareReplay(1));
